import { UntilDestroy } from "@ngneat/until-destroy";
import { Injectable, signal } from "@angular/core";
import { BehaviorSubject, combineLatest, Observable, of } from "rxjs";
import { GeneralSelectionFacade } from "../../general-selection/facade/general-selection.facade";
import { PortfoliosFacade } from "../../portfolios/facade/portfolios.facade";
import { catchError, finalize, map } from "rxjs/operators";
import { IReinvestmentInstructionViewModel } from "../../../models/termDeposits/reinvestment-instruction";
import { ReinvestmentService } from "../../../services/http/reinvestment.service";
import { SnackBarService } from "../../../services/snackbar/snackBar.service";
import { RolloverOptions } from "../../../models/termDeposits/rollover-options.enum";
import { DecimalPipe } from "@angular/common";
import { humanizeEnum } from "../../../services/utils/humanize-enum";
import { deduplicatingSwitchMap, sharedDeduplicatingSwitchMap } from "src/app/utils/deduplicatingSwitchMap";

@UntilDestroy()
@Injectable()
export class ReinvestmentFacade {


    /**
     * Just a subject that we can fire events to trigger loading of reinvestment instructions. Only used internally.
     */
    private loadReinvestmentInstructionsSubject = new BehaviorSubject(null);

    public loadingReinvestmentInstructions = signal(false);

    public reinvestmentInstructions$: Observable<IReinvestmentInstructionViewModel[]> = combineLatest([
        this.generalSelectionfacade.getSelectedEntityPortfolioState(),
        this.loadReinvestmentInstructionsSubject.asObservable()
    ]).pipe(
        map(([generalState, _]) => 
            generalState.selectedPortfolios.map(x => x.id)),
        sharedDeduplicatingSwitchMap(portfolioIds => {
            
            if (portfolioIds.length === 0) {
                return of([]);
            }
            
            return this.getReinvestmentInstructions(portfolioIds);
        }));

    constructor(
        private generalSelectionfacade: GeneralSelectionFacade,
        private portfolioFacade: PortfoliosFacade,
        private reinvestmentService: ReinvestmentService,
        private snackBarService: SnackBarService,
        private decimalPipe: DecimalPipe
    ) {
    }
    
    public loadReinvestmentInstructions(){
        this.loadReinvestmentInstructionsSubject.next(null);
    }

    private getReinvestmentInstructions(portfolioIds: number[]): Observable<IReinvestmentInstructionViewModel[]> {
        this.loadingReinvestmentInstructions.set(true);
        return this.reinvestmentService.getReinvestmentInstructions(portfolioIds).pipe(
            catchError((res) => {
                this.snackBarService.showWarningSnackBar(
                    'Failed to get term deposits. Please try again later.',
                );
                return of();
            }),
            finalize(() => {
            this.loadingReinvestmentInstructions.set(false);
        }));
    }

    /**
     * Retrieves portfolio ids based on either selectedPortfolios or the portfolios of selectedEntities from the state.
     */
    private getPortfolioIds(
        selectedPortfolios,
        portfoliosOfSelectedEntities,
    ): number[] {
        return selectedPortfolios.length > 0
            ? selectedPortfolios.map((x) => x.id)
            : portfoliosOfSelectedEntities.map((x) => x.id);
    }

    public editReinvestmentInstruction(instructionId: number, portfolioId: number, reinvestmentInstruction: IReinvestmentInstructionViewModel, durationName: string) {
        this.loadingReinvestmentInstructions.set(true);
        return this.reinvestmentService.editReinvestmentInstruction(instructionId, portfolioId, reinvestmentInstruction).pipe(
            map(res => {
                this.snackBarService.showSnackBar(`Success! Your term deposit instruction - <strong>Invest ${this.decimalPipe.transform(reinvestmentInstruction.amount.value, '1.2-2')} ${reinvestmentInstruction.amount.currencyCode}, ${durationName} Term Deposit, ${this.decimalPipe.transform(reinvestmentInstruction.interestRate, '1.2-2')}%</strong>
                    - has been updated to be <strong>${humanizeEnum(RolloverOptions[reinvestmentInstruction.rollOverOptions])}</strong>`);
            }),
            catchError((res) => {
                this.snackBarService.showWarningSnackBar(
                    'Failed to edit term deposit. Please try again later.',
                );
                return of();
            }),
            finalize(() => {
                this.loadingReinvestmentInstructions.set(false);
                this.loadReinvestmentInstructions();
            })
        );
    }
}